/* ===================================================================
   TriggerArmConfirm -- the ONE plain-English "arm the review trigger" confirm
   surfaced INLINE when a tool is connected (2026-07-09). Per the forward-
   trigger build brief: the best tools do not ask the owner a technical
   "job-done vs paid" question -- they pre-pick a sensible default from the
   business type + the connected software and offer at most ONE alternative,
   in plain English.

   This is the ONBOARDING-side SCAFFOLD only. The deep forward-trigger backend
   (canonical event model, idempotency, fire-to-all) is a separate build
   (Hopper, engine/). Here we just surface the confirm when a connector is live
   and record the owner's choice locally (best-effort) so the backend can read
   it later; nothing is wired to send yet.

   NEVER a technical question. NEVER fires on booked / scheduled / quote / unpaid
   invoice -- the options below are completion/payment events only.
   =================================================================== */
import { useState } from 'react';
import { track } from '../lib/analytics';

type TriggerMode = 'JC' | 'IP';
interface TriggerOption {
  mode: TriggerMode;
  /* Plain-English clause; reads as "...automatically <label>." */
  label: string;
}
interface SourceTrigger {
  sourceName: string;
  default: TriggerOption;
  /* At most ONE alternative (the brief: a default + one alternative, never a
     menu). Omitted for pay-at-service sources where payment IS completion. */
  alt?: TriggerOption;
}

/* Per-connected-source presets (forward-trigger brief section 5). Adding a
   source is a one-line config edit, not new logic. */
const SOURCE_TRIGGERS: Record<string, SourceTrigger> = {
  jobber: {
    sourceName: 'Jobber',
    default: { mode: 'JC', label: 'when a job is marked complete' },
    alt: { mode: 'IP', label: 'when the invoice is paid' },
  },
  housecall: {
    sourceName: 'Housecall Pro',
    default: { mode: 'JC', label: 'when a job is marked complete' },
    alt: { mode: 'IP', label: 'when the invoice is paid' },
  },
  square: {
    sourceName: 'Square',
    default: { mode: 'IP', label: 'when the customer pays' },
  },
  quickbooks: {
    sourceName: 'QuickBooks',
    default: { mode: 'IP', label: 'when the invoice is paid' },
  },
  calendly: {
    sourceName: 'Calendly',
    default: { mode: 'JC', label: 'when the appointment is completed' },
  },
};

const ARMED_PREFIX = 'rhody_trigger_armed_';

function armedKey(clientId: string | undefined, source: string): string | null {
  return clientId ? `${ARMED_PREFIX}${clientId}_${source}` : null;
}
function readArmed(clientId: string | undefined, source: string): TriggerMode | null {
  const k = armedKey(clientId, source);
  if (!k) return null;
  try {
    const raw = localStorage.getItem(k);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.mode === 'JC' || parsed?.mode === 'IP' ? parsed.mode : null;
  } catch {
    return null;
  }
}
function writeArmed(clientId: string | undefined, source: string, mode: TriggerMode): void {
  const k = armedKey(clientId, source);
  if (!k) return;
  try {
    localStorage.setItem(k, JSON.stringify({ mode, at: new Date().toISOString() }));
  } catch {
    /* non-fatal */
  }
}

export function TriggerArmConfirm({
  source,
  clientId,
}: {
  source: string;
  clientId: string | undefined;
}) {
  const cfg = SOURCE_TRIGGERS[source];
  const [armedMode, setArmedMode] = useState<TriggerMode | null>(() =>
    readArmed(clientId, source),
  );
  // Which option is currently selected in the UI (defaults to the preset).
  const [useAlt, setUseAlt] = useState(false);

  if (!cfg) return null;

  const chosen = useAlt && cfg.alt ? cfg.alt : cfg.default;

  function arm() {
    writeArmed(clientId, source, chosen.mode);
    setArmedMode(chosen.mode);
    track('trigger_armed', { source, mode: chosen.mode });
  }

  if (armedMode) {
    const armedLabel =
      armedMode === cfg.default.mode ? cfg.default.label : cfg.alt?.label ?? cfg.default.label;
    return (
      <div className="trigarm trigarm--done" role="status">
        <span className="trigarm__check" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.6}>
            <path d="M5 12l5 5L20 6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        <div className="trigarm__body">
          {/* Truthful: this SAVES A PREFERENCE. The trigger starts firing once
              the connector is live (and, for texts, once carrier approval
              clears) -- we do NOT claim it is already running. */}
          <b>Review-request trigger saved.</b>
          <span>
            Once {cfg.sourceName} is connected and live, Rhody Reviews will ask
            each customer for a review {armedLabel}. You can change this anytime
            in Settings.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="trigarm">
      <div className="trigarm__lab">{cfg.sourceName} connected</div>
      <p className="trigarm__q">
        Rhody Reviews will ask each customer for a review automatically{' '}
        <b>{chosen.label}</b> in {cfg.sourceName}.
      </p>
      <div className="trigarm__actions">
        <button className="btn btn--primary" onClick={arm}>
          Sounds good, turn it on
        </button>
        {cfg.alt && (
          <button
            className="btn btn--ghost trigarm__alt"
            onClick={() => setUseAlt((v) => !v)}
          >
            {useAlt
              ? `Actually, ask ${cfg.default.label}`
              : `Prefer to wait ${cfg.alt.label}?`}
          </button>
        )}
      </div>
    </div>
  );
}

export default TriggerArmConfirm;
